import { useQuery } from '@tanstack/react-query';
import { matchSectors, getSectorTree } from '@/api/bankData.api';

export const bankDataKeys = {
  all: ['bank-data'] as const,
  match: (industry: string, category: string) =>
    [...bankDataKeys.all, 'match', industry.toLowerCase(), category.toLowerCase()] as const,
  tree: (sectorId: number) => [...bankDataKeys.all, 'tree', sectorId] as const,
  jabatans: (industry: string, category: string) =>
    [...bankDataKeys.all, 'jabatans', industry.toLowerCase(), category.toLowerCase()] as const,
};

/**
 * Fetches bank-data jabatans for the given company industry/category.
 * Composes /match → /tree calls, flattens jabatans across all matched sectors,
 * dedupes by lowercased name, sorts alphabetically.
 *
 * Disabled (no fetch) when industry is empty/undefined.
 */
export function useBankDataJabatans(industry: string | undefined, category: string | undefined) {
  const enabled = !!industry;
  const query = useQuery({
    queryKey: bankDataKeys.jabatans(industry ?? '', category ?? ''),
    queryFn: async () => {
      const match = await matchSectors({ industry: industry!, category: category ?? '' });
      if (match.matched.length === 0) return [];
      const trees = await Promise.all(match.matched.map(s => getSectorTree(s.id)));
      const namesByKey = new Map<string, string>();
      for (const tree of trees) {
        for (const j of tree.jabatans) {
          const name = j.name.trim();
          const key = name.toLowerCase();
          if (!namesByKey.has(key)) {
            namesByKey.set(key, name);
          }
        }
      }
      return Array.from(namesByKey.values()).sort((a, b) => a.localeCompare(b));
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  // In React Query v4, a disabled query has status 'loading' (no data yet),
  // so isLoading is true even when not fetching. Normalise to false when disabled.
  return { ...query, isLoading: enabled ? query.isLoading : false };
}
